import { useMemo } from 'react';
import { useRouter } from 'next/router';
import Redirect from 'helpers/redirect';
import { useFeatureFlags, FEATURE_FLAG_LIST } from 'hooks';
import Login, { LoginProps } from 'ui/authentication/login';
import { useAccount } from 'frontastic/hooks';
import { calculateDaysFromToday } from 'helpers/utils/datePicker';
import { PASSWORD_EXPIRED_THRESHOLD } from 'helpers/constants';

type Props = {
  data: LoginProps & {
    isCompanyProfileMissing?: boolean;
  };
};

export enum SUCCESS_MESSAGE_TYPE {
  CHANGE_PASSWORD_NOTIFICATION = 'changePasswordNotification',
}

const AccountLoginTastic = ({ data }: Props) => {
  const router = useRouter();
  const { isFeatureEnabled } = useFeatureFlags();

  if (router.query.changePasswordNotification) {
    data.successMessage = { type: SUCCESS_MESSAGE_TYPE.CHANGE_PASSWORD_NOTIFICATION };
  }

  const { loggedIn, account, isCoordinatorMissingCompanyProfile } = useAccount();

  const shouldNavigateToChangePassword = useMemo(() => {
    if (!account?.custom?.fields?.password_created_at) {
      return false;
    }
    const daysPassed = calculateDaysFromToday(account.custom.fields.password_created_at);
    return daysPassed > PASSWORD_EXPIRED_THRESHOLD;
  }, [account?.custom?.fields?.password_created_at]);

  const redirectLink = useMemo(() => {
    const lvp = router?.query?.lvp ? router.query.lvp.toString() : '';
    const baseUrl = lvp ? `/${lvp}` : `/`;
    const pwdParam = router?.query?.changePasswordNotification ? '?changePasswordNotification=true' : '';
    return `${baseUrl}${pwdParam}`;
  }, [router.query.lvp, router.query.changePasswordNotification]);
  if (loggedIn) {
    // We are redirecting to change password if the password is expired directly from here
    // to avoid flaky behavior when there is some delay in MC.
    if (shouldNavigateToChangePassword && isFeatureEnabled(FEATURE_FLAG_LIST.PASSWORD_EXPIRY)) {
      return <Redirect target={`/change-password`} />;
    }

    return isCoordinatorMissingCompanyProfile && isFeatureEnabled(FEATURE_FLAG_LIST.ORDER_ON_BEHALF_BU_SELECTION) ? (
      <Redirect target="/business-unit" />
    ) : (
      <Redirect target={redirectLink} />
    );
  }

  return <Login {...data} />;
};

export default AccountLoginTastic;
